import React, { useEffect, useRef, useState, useMemo } from 'react';
import css from './LocationAutocomplete.module.css';
import { countryCodeMap } from '../../util/configOptions';

// To use the get place details api
// The google lib (specifically the map) needs a div to attach itself to
// So an empty (hidden) div was added
const ghostDivForGoogleMapsSearch = 'ghost-div-for-gmap-search';

export const LocationAutocomplete = props => {
  const {
    handleLocationUpdate,
    country,
    placeholder,
    className,
    countryCode,
    includeStreetNumber,
  } = props;

  const inputRef = useRef(null);
  const [inputValue, setInputValue] = useState(props.input.value);
  const [isSelectionInProgress, setIsSelectionInProgress] = useState(false);

  const getComponentRestrictions = () => {
    if (countryCode) {
      // If a country code is provided, return it
      return { country: countryCode };
    } else if (country) {
      // If a country is provided, find the country code
      return { country: countryCodeMap[country] };
    }
    // If there are no restrictions, return all countries
    return { country: Object.values(countryCodeMap) };
  };
  const autocompleteRef = useRef(null);
  const componentRestrictions = useMemo(() => getComponentRestrictions(), [countryCode, country]); // Memoize this value

  const typeRestrictions = ['address'];

  const handleAutocompleteDefocusEvent = () => {
    // If selection is in progress, don't run the blur handler
    // This prevents the race condition where blur fires before place_changed
    if (isSelectionInProgress) {
      setIsSelectionInProgress(false);
      return;
    }

    const service = new google.maps.places.AutocompleteService();
    service.getPlacePredictions(
      {
        input: inputRef.current.value,
        componentRestrictions,
        types: typeRestrictions,
      },
      (prediction, predictionStatus) => {
        if (predictionStatus === google.maps.places.PlacesServiceStatus.OK) {
          const predictionId = prediction[0].place_id;
          const map = new window.google.maps.Map(
            document.getElementById(ghostDivForGoogleMapsSearch)
          );

          // Initialize the Places Service
          const placesService = new window.google.maps.places.PlacesService(map);
          placesService.getDetails({ placeId: predictionId }, (place, placeStatus) => {
            if (placeStatus === google.maps.places.PlacesServiceStatus.OK) {
              handlePlaceChange(place);
            }
          });
        } else {
          // If no predictions were found, still update with the manual input
          const currentValues = {
            street: inputValue,
            streetNumber: props.input.streetNumber || '',
            city: props.input.city || '',
            zipCode: props.input.zipCode || '',
          };

          props.input.onChange(inputValue);
          handleLocationUpdate(currentValues);
        }
      }
    );
  };

  const handlePlaceChange = place => {
    const street = place.address_components.find(address => address.types.includes('route'))
      ?.long_name;
    const city = place.address_components.find(address => address.types.includes('locality'))
      ?.long_name;
    const zipCode = place.address_components.find(address => address.types.includes('postal_code'))
      ?.long_name;
    const streetNumber = place.address_components.find(address =>
      address.types.includes('street_number')
    )?.long_name;

    // Update form and other fields
    const displayValue = includeStreetNumber
      ? `${street || ''}${streetNumber ? ' ' + streetNumber : ''}`
      : street || '';

    setInputValue(displayValue);
    // Update the input field in the form
    props.input.onChange(displayValue);

    // Update all the related fields
    handleLocationUpdate({
      street: street || '',
      streetNumber: streetNumber || '',
      city,
      zipCode,
    });

    // Reset selection flag after handling place change
    setIsSelectionInProgress(false);
  };

  // Hook to monitor changes in the props input value
  useEffect(() => {
    setInputValue(props.input.value);
  }, [props.input.value]);

  // Hook to mount the autocomplete component and
  useEffect(() => {
    if (!inputRef.current) return;
    const options = {
      types: typeRestrictions,
      componentRestrictions,
    };
    autocompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, options);

    // Set a flag when selection starts to prevent blur handler from running too early
    const onPlaceChangedStarted = () => {
      setIsSelectionInProgress(true);
    };

    // Add event listener for when user interacts with the autocomplete dropdown
    if (inputRef.current) {
      inputRef.current.addEventListener('click', onPlaceChangedStarted);
      inputRef.current.addEventListener('keydown', onPlaceChangedStarted);
    }

    // Listener to handle autocomplete results
    autocompleteRef.current.addListener('place_changed', () => {
      const place = autocompleteRef.current.getPlace();
      handlePlaceChange(place);
    });

    return () => {
      if (inputRef.current) {
        inputRef.current.removeEventListener('click', onPlaceChangedStarted);
        inputRef.current.removeEventListener('keydown', onPlaceChangedStarted);
      }
      if (autocompleteRef.current) {
        autocompleteRef.current.unbindAll();
        google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, [country, countryCode, componentRestrictions]);

  const handleInputChange = event => {
    const newValue = event.target.value;
    setInputValue(newValue);
    // Update the form input value to ensure the form validation works
    props.input.onChange(newValue);
  };

  const containerClassName = className ? className : css.locationAutocompleteContainer;

  return (
    <div className={containerClassName}>
      <div id={ghostDivForGoogleMapsSearch} className={css.ghostDiv}></div>
      <input
        ref={inputRef}
        type="text"
        placeholder={placeholder}
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleAutocompleteDefocusEvent}
      />
    </div>
  );
};
